<template>
	<div class="PersonalisationFields" v-if="ticketPersonalisations"
		style="display: flex; flex-direction: column; gap: 12px;"
	>
		<Field :title="$t('text.fixedPersonalisationFields')" :infoText="$t('text.fixedPersonalisationHelp')">
			<div v-for="fixedTicketPersonalisation of possibleFixedTicketPersonalisationsSorted" :key="fixedTicketPersonalisation.sys.id"
				style="display: flex; gap: 4px; align-items: center;"
			>
				<v-checkbox hide-details density="compact"
					:disabled="disabled"
					v-model="fixedTicketPersonalisations"
					:label="fixedTicketPersonalisation.fields.personalisationGroup.de.fields.title[ selectedLocale ]"
					:value="fixedTicketPersonalisation"
					:value-comparator="(a, b) => a.fields.personalisationGroup.de.sys.id === b.fields.personalisationGroup.de.sys.id"
					style="flex-grow: 0;"
				/>
			</div>
		</Field>
		<Field :title="$t('text.additionalPersonalisationFields')" :infoText="$t('text.additionalPersonalisationHelp')">
			<div v-if="additionalTicketPersonalisations?.length"
				style="margin: 10px 0; display: flex; flex-direction: column; gap: 10px;"
			>
				<div v-for="(additionalPersonalisation, p) in additionalTicketPersonalisations" :key="p"
					style="display: flex; gap: 10px; align-items: center; flex-wrap: wrap;"
				>
					<PersonalisationField v-if="additionalPersonalisation?.fields?.personalisationGroup?.de?.fields"
						:additionalPersonalisation="additionalPersonalisation"
						@remove="removePersonalisation(additionalPersonalisation)"
						@validation-change="(isValid) => handleValidationChange(additionalPersonalisation.sys.id, isValid)"
					/>
				</div>
			</div>
			<v-btn class="gradientButton" elevation="0" @click="addAdditionalPersonalisationRow()">
				<v-icon>mdi-plus</v-icon>{{ $t('text.addPersonalisationOption') }}
			</v-btn>
		</Field>
		<br />
		<br />
		<br />
	</div>
</template>

<script>
import Common from '@/mixins/Common.vue'
import PersonalisationField from './PersonalisationField.vue'

export default {
	name: 'PersonalisationFields',
	mixins: [ Common ],
	components: { PersonalisationField },
	props: {
		ticketPersonalisations: Array, // Array of PersonalisationGroups. Each group contains PersonalisationFields.
		disabled: Boolean,
	},
	data: () => ({
		possibleFixedTicketPersonalisations: [],
		validationStates: {},
	}),
	computed: {
		possibleFixedGroupIds() {
			return this.possibleFixedTicketPersonalisations.map(item => item.fields.personalisationGroup.de.sys.id)
		},
		fixedTicketPersonalisations: {
			get() {
				return this.ticketPersonalisations.filter(item => this.possibleFixedGroupIds.includes(item.fields.personalisationGroup.de.sys.id))
			},
			set(value) {
				this.ticketPersonalisations.splice(0, this.ticketPersonalisations.length, ...value, ...this.additionalTicketPersonalisations)
			},
		},
		additionalTicketPersonalisations() {
			return this.ticketPersonalisations.filter(item => !this.possibleFixedGroupIds.includes(item.fields.personalisationGroup.de.sys.id))
		},
		possibleFixedTicketPersonalisationsSorted() {
			return this.possibleFixedTicketPersonalisations.sort((a, b) => {
				if (a.fields.personalisationGroup.de.fields.title.de === 'Name') return -1
				if (b.fields.personalisationGroup.de.fields.title.de === 'Name') return 1
				return a.fields.personalisationGroup.de.fields.title.de.localeCompare(b.fields.personalisationGroup.de.fields.title.de)
			})
		},
		isValid() {
			return Object.values(this.validationStates).every(state => state === true)
		},
	},
	watch: {
		ticketPersonalisations: {
			deep: true,
			immediate: true,
			handler() {
				this.initValidationStates()
			},
		},
	},
	methods: {
		initValidationStates() {
			this.refreshValidationStates()
			for (const tp of this.ticketPersonalisations ?? []) {
				const id = tp?.sys?.id
				if (!id) continue
				if (!(id in this.validationStates)) {
					this.validationStates[id] = true
				}
			}
			this.$emit('validation-change', this.isValid)
		},
		handleValidationChange(id, isValid) {
			this.validationStates[id] = isValid
			this.$emit('validation-change', this.isValid)
		},
		addAdditionalPersonalisationRow() {
			const newId = 'id_p_' + Math.round(Math.random() * 99999)
			this.ticketPersonalisations.push({
				sys: {
					id: newId
				},
				fields: {
					title: { de: '', en: '', fr: '', it: '', nl: '' },
					required: { de: false },
					personalisationGroup: {
						de: {
							sys: { id: '' },
							fields: {
								title: { de: '', en: '', fr: '', it: '', nl: '' },
								personalisationFields: {
									de: [{
										fields: {
											inputType: { de: 'Text' },
											label: { de: '', en: '', fr: '', it: '', nl: '' },
											options: { de: [] }
										}
									}]
								}
							}
						}
					}
				}
			})
			// Initialize validation state as false for new rows (empty fields are invalid)
			this.validationStates[newId] = false
		},
		removePersonalisation(customPersonalisation) {
			const index = this.ticketPersonalisations.indexOf(customPersonalisation)
			if (index < 0) return
			this.ticketPersonalisations.splice(index, 1)
			// Remove validation state for deleted personalisation
			delete this.validationStates[customPersonalisation.sys.id]
			this.refreshValidationStates()
			this.$emit('validation-change', this.isValid)
		},
		refreshValidationStates() {
			const validIds = new Set((this.ticketPersonalisations ?? [])
				.map(tp => tp?.sys?.id)
				.filter(Boolean)
			)
			for (const id of Object.keys(this.validationStates)) {
				if (!validIds.has(id)) {
					delete this.validationStates[id]
				}
			}
		},
		wrapTicketPersonalisation(personalisationGroup) {
			return {
				sys: {
					id: 'id_p_' + Math.round(Math.random() * 99999)
				},
				fields: {
					personalisationGroup: { de: personalisationGroup },
					// TODO: we force required here - is that ok? shouldnt the user have control?
					required: { de: true },
				},
			}
		},
	},
	async mounted() {
		// Set possibleFixedTicketPersonalisations
		if (this.$store.state.coreConfig.fields.personalisationGroups.de?.length > 0) {
			for (const personalisationGroup of this.$store.state.coreConfig.fields.personalisationGroups.de) {
				let ftp = this.ticketPersonalisations.find(tp => tp.fields.personalisationGroup.de.sys.id == personalisationGroup.sys.id)
				if (!ftp) {
					// Render the checkbox for every fixed group, but don't force-select any
					// (incl. Name). Selection state reflects ticketPersonalisations only.
					ftp = this.wrapTicketPersonalisation(personalisationGroup)
				}
				this.possibleFixedTicketPersonalisations.push(ftp)
			}
		}
		this.initValidationStates()
	},
}
</script>

<style scoped lang="scss">
.v-list-item .v-list-item__subtitle, .v-list-item .v-list-item__title {
	line-height: 2 !important;
}
div.field .v-input { margin-top: 0; }
table { margin-top: 15px; margin-bottom: 15px; padding: 10px; width: 100%; }
table th { text-align: left; }
table td { padding-right: 10px; vertical-align: bottom; }
</style>
